import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useBooking } from "../../context/BookingContext";
import { Link } from "react-router-dom";
import { Box, Typography, Grid, CircularProgress, Button } from "@mui/material";
import moment from "moment";

const Reservations = () => {
  const { user, loading: authLoading } = useContext(AuthContext);
  const { fetchOwnerReservations } = useBooking();
  const [reservations, setReservations] = useState([]);
  const [reservationsLoaded, setReservationsLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    const fetchReservations = async () => {
      if (user?.userID) {
        try {
          const ownerReservations = await fetchOwnerReservations();
          setReservations(ownerReservations.reverse());
          setReservationsLoaded(true);
        } catch (error) {
          console.error("Error fetching reservations:", error);
          setReservations([]);
          setReservationsLoaded(true);
        }
      }
    };
    fetchReservations();
  }, [fetchOwnerReservations, user?.userID]);

  // Calculate counts dynamically from reservations
  const now = moment();
  const counts = {
    all: reservations.length,
    reservations: reservations.length,
    upcoming: reservations.filter((res) =>
      moment.utc(res.CheckinTimestamp).local().isAfter(now)
    ).length,
    completed: reservations.filter((res) =>
      moment.utc(res.CheckoutTimestamp).local().isBefore(now)
    ).length,
    canceled: reservations.filter(
      (res) => res.Status?.toLowerCase() === "cancelled"
    ).length,
  };

  if (authLoading || !reservationsLoaded) {
    return (
      <Box className="loading" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box className="loading" sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          Please log in or sign up to view your reservations.
        </Typography>
      </Box>
    );
  }

  // Filter reservations based on active tab
  const filteredReservations = reservations.filter((res) => {
    if (activeTab === "all") return true;
    if (activeTab === "reservations") return true;
    if (activeTab === "upcoming")
      return moment.utc(res.CheckinTimestamp).local().isAfter(now);
    if (activeTab === "completed")
      return moment.utc(res.CheckoutTimestamp).local().isBefore(now);
    if (activeTab === "canceled")
      return res.Status?.toLowerCase() === "cancelled";
    return true;
  });

  return (
    <Box
      className="reservations-section"
      sx={{ maxWidth: "1200px", mx: "auto", bgcolor: "#ffffff" }}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" color="text.primary">
            Your Reservations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All reservations ({counts.all})
          </Typography>
        </Box>
        <Box className="reservation-tabs" sx={{ mb: 2 }}>
          {["all", "reservations", "upcoming", "completed", "canceled"].map(
            (tab) => (
              <Button
                key={tab}
                className={`reservation-tab ${
                  activeTab === tab ? "active" : ""
                }`}
                variant="outlined"
                onClick={() => setActiveTab(tab)}
                sx={{
                  mr: 1,
                  borderRadius: "25px",
                  py: "6px",
                  px: "12px",
                }}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)} ({counts[tab]})
              </Button>
            )
          )}
        </Box>
        {filteredReservations.length === 0 ? (
          <Box
            className="reservation-content"
            sx={{
              bgcolor: "#f5f5f5",
              borderRadius: "8px",
              p: 4,
              textAlign: "center",
            }}
          >
            <Typography variant="body1" color="text.secondary">
              You don’t have any{" "}
              {activeTab === "all" ? "reservations" : activeTab.toLowerCase()}{" "}
              at the moment.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredReservations.map((reservation) => (
              <Grid item xs={12} sm={6} md={4} key={reservation.BookingID}>
                <Box className="reservation-card">
                  <Box className="card-header" sx={{ mb: 2 }}>
                    <Typography variant="h6" color="text.primary">
                      <Link
                        to={`/kitchen/${reservation.KitchenID}`}
                        style={{ color: "#2c3e50", textDecoration: "none" }}
                      >
                        {reservation.KitchenTitle}
                      </Link>
                    </Typography>
                    <span
                      className={`status-badge status-${reservation.Status?.toLowerCase()}`}
                    >
                      {reservation.Status}
                    </span>
                  </Box>
                  <Box className="card-content">
                    <Box className="time-section" sx={{ mb: 2 }}>
                      <Box
                        className="time-item"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" className="label">
                          Check-in
                        </Typography>
                        <Typography variant="body2" className="value">
                          {moment
                            .utc(reservation.CheckinTimestamp)
                            .local()
                            .format("MMM DD, YYYY HH:mm")}
                        </Typography>
                      </Box>
                      <Box
                        className="time-item"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" className="label">
                          Check-out
                        </Typography>
                        <Typography variant="body2" className="value">
                          {moment
                            .utc(reservation.CheckoutTimestamp)
                            .local()
                            .format("MMM DD, YYYY HH:mm")}
                        </Typography>
                      </Box>
                    </Box>
                    <Box className="details-section" sx={{ mb: 2 }}>
                      <Box
                        className="detail-item"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" className="label">
                          Duration
                        </Typography>
                        <Typography variant="body2" className="value">
                          {reservation.BookingType === "hour"
                            ? `${reservation.TotalHours} hr${
                                reservation.TotalHours > 1 ? "s" : ""
                              }`
                            : `${reservation.TotalDays || 1} day${
                                (reservation.TotalDays || 1) > 1 ? "s" : ""
                              }`}
                        </Typography>
                      </Box>
                    </Box>
                    <Box className="price-section">
                      <Box
                        className="price-item"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" className="label">
                          Base
                        </Typography>
                        <Typography variant="body2" className="value">
                          ${reservation.BaseAmount?.toFixed(2)}
                        </Typography>
                      </Box>
                      <Box
                        className="price-item"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" className="label">
                          Fees
                        </Typography>
                        <Typography variant="body2" className="value">
                          ${reservation.ServiceFee?.toFixed(2)}
                        </Typography>
                      </Box>
                      {reservation.SecurityDeposit > 0 && (
                        <Box
                          className="price-item"
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2" className="label">
                            Deposit
                          </Typography>
                          <Typography variant="body2" className="value">
                            ${reservation.SecurityDeposit.toFixed(2)}
                          </Typography>
                        </Box>
                      )}
                      <Box
                        className="price-item total"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          borderTop: "1px solid #e0e0e0",
                          pt: 1,
                        }}
                      >
                        <Typography variant="body2" className="label">
                          Total
                        </Typography>
                        <Typography variant="body2" className="value">
                          ${reservation.TotalAmount?.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default Reservations;
